import * as React from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "@tanstack/react-router";
import { cn } from "../ui/index.ts";
import { I } from "../icons/icons.tsx";
import {
  collectPanelIds,
  createPanelInstance,
  getBoutiqueEntries,
  PanelDefs,
  useDragCtx,
  type BoutiqueEntry,
  type BoutiqueVariant,
} from "./docking.component.tsx";
import {
  headerBtnAccent,
  headerBtnDanger,
  headerBtnSecondary,
} from "../page-layout.ts";
import {
  useDockingEditBarHost,
  usePendingRename,
  useSidebarEditMode,
} from "./docking-edit-bar-host.tsx";
import {
  removeUserModule,
  renameUserModule,
  useModulesConfig,
} from "../../core/services/modules-config.service.ts";
import {
  paths,
  usePracticeUuid,
} from "../../core/services/navigation.service.ts";
import { usePromptDialog } from "../../core/providers/prompt-dialog.provider.tsx";

/**
 * Bandeau d'édition docking : se rend uniquement dans le slot de la sidebar
 * (sous le module actif en mode Modifier). Pas de fallback flottant.
 */
export function DockingEditBar() {
  const ctx = useDragCtx();
  const host = useDockingEditBarHost();
  const { setSidebarEditMode } = useSidebarEditMode();
  const { pendingRenameModuleId, setPendingRenameModuleId } =
    usePendingRename();
  const params = useParams({ strict: false }) as { userModuleId?: string };
  const config = useModulesConfig();
  const dialog = usePromptDialog();
  const navigate = useNavigate();
  const practiceUuid = usePracticeUuid();
  const userModule = params.userModuleId
    ? config.userModules.find((m) => m.id === params.userModuleId)
    : null;
  if (!ctx.editMode || !host) return null;

  const onReset = async () => {
    const ok = await dialog.confirm({
      title: "Réinitialiser la disposition",
      message:
        "Toutes vos personnalisations seront définitivement perdues et la disposition par défaut sera restaurée.",
      confirmLabel: "Réinitialiser",
      danger: true,
    });
    if (ok) ctx.actions.resetLayout();
  };

  const onDelete = async () => {
    if (!userModule) return;
    const ok = await dialog.confirm({
      title: `Supprimer « ${userModule.label} »`,
      message:
        "Ce module et toute sa configuration de widgets seront définitivement supprimés.",
      confirmLabel: "Supprimer",
      danger: true,
    });
    if (!ok) return;
    setSidebarEditMode(false);
    removeUserModule(userModule.id);
    navigate({ to: paths.dashboard(practiceUuid) });
  };

  const onCancel = () => {
    ctx.actions.cancelEdit();
    setSidebarEditMode(false);
  };

  const onCommit = () => {
    ctx.actions.commitEdit();
    setSidebarEditMode(false);
  };

  return createPortal(
    <div className="flex flex-col gap-1.5 rounded-md border border-sf-primary-100 bg-sf-primary-50/40 p-1.5">
      <div className="px-1 text-[9.5px] font-bold uppercase tracking-wider text-sf-primary/80">
        Édition docking
      </div>
      {userModule && (
        <RenameField
          value={userModule.label}
          onSave={(v) => renameUserModule(userModule.id, v)}
          autoFocus={pendingRenameModuleId === userModule.id}
          onAutoFocusConsumed={() => setPendingRenameModuleId(null)}
        />
      )}
      <AddPanelMenu variant="sidebar" />
      {userModule ? (
        <button
          type="button"
          onClick={onDelete}
          title="Supprimer ce module"
          className={cn(
            headerBtnDanger,
            "w-full justify-center px-1.5 py-1 text-[11.5px]",
          )}
        >
          <I.Trash size={11} /> Supprimer
        </button>
      ) : (
        <button
          type="button"
          onClick={onReset}
          title="Réinitialiser la disposition par défaut"
          className={cn(
            headerBtnDanger,
            "w-full justify-center px-1.5 py-1 text-[11.5px]",
          )}
        >
          <I.Trash size={11} /> Réinitialiser
        </button>
      )}
      <div className="grid grid-cols-2 gap-1">
        <button
          type="button"
          onClick={onCancel}
          title="Annuler les modifications en cours"
          className={cn(
            headerBtnSecondary,
            "w-full justify-center px-1.5 py-1 text-[11.5px]",
          )}
        >
          <I.Undo size={11} /> Annuler
        </button>
        <button
          type="button"
          onClick={onCommit}
          title="Sauvegarder et terminer la personnalisation"
          className={cn(
            headerBtnAccent,
            "w-full justify-center px-1.5 py-1 text-[11.5px]",
          )}
        >
          <I.Check size={11} /> Terminer
        </button>
      </div>
    </div>,
    host,
  );
}

function RenameField({
  value,
  onSave,
  autoFocus = false,
  onAutoFocusConsumed,
}: {
  value: string;
  onSave: (next: string) => void;
  autoFocus?: boolean;
  onAutoFocusConsumed?: () => void;
}) {
  const [draft, setDraft] = React.useState(value);
  const lastSavedRef = React.useRef(value);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setDraft(value);
    lastSavedRef.current = value;
  }, [value]);

  React.useEffect(() => {
    if (!autoFocus) return;
    const el = inputRef.current;
    if (!el) return;
    // Légère défer pour s'assurer que l'élément est bien monté dans le portal.
    const t = window.setTimeout(() => {
      el.focus();
      el.select();
      onAutoFocusConsumed?.();
    }, 0);
    return () => window.clearTimeout(t);
  }, [autoFocus, onAutoFocusConsumed]);

  const commit = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === lastSavedRef.current) return;
    onSave(trimmed);
    lastSavedRef.current = trimmed;
  };

  return (
    <div className="flex flex-col gap-1 px-0.5">
      <label
        htmlFor="docking-edit-rename"
        className="text-[9.5px] font-semibold uppercase tracking-wider text-sf-text-muted"
      >
        Nom du module
      </label>
      <input
        id="docking-edit-rename"
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            (e.target as HTMLInputElement).blur();
          } else if (e.key === "Escape") {
            setDraft(lastSavedRef.current);
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder="Nom du module"
        aria-label="Renommer le module"
        className="h-7 w-full rounded-sm border-0 border-b border-dotted border-sf-text-muted/40 bg-transparent px-1 font-[inherit] text-[13px] font-semibold text-sf-text outline-none transition-colors focus:border-solid focus:border-sf-primary focus:bg-white placeholder:text-sf-text-muted placeholder:font-normal"
      />
    </div>
  );
}

const WIDGET_MENU_WIDTH = 240;
const SUBMENU_WIDTH = 200;

interface AvailableEntry {
  entry: BoutiqueEntry;
  variants: BoutiqueVariant[];
}

function AddPanelMenu({ variant }: { variant?: "sidebar" } = {}) {
  const ctx = useDragCtx();
  const [open, setOpen] = React.useState(false);
  const [menuStyle, setMenuStyle] = React.useState<{
    top: number;
    left: number;
  } | null>(null);
  const [expanded, setExpanded] = React.useState<{
    entry: BoutiqueEntry;
    top: number;
    left: number;
  } | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const submenuRef = React.useRef<HTMLDivElement>(null);

  const updateMenuPosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const left = Math.min(
      Math.max(8, rect.right - WIDGET_MENU_WIDTH),
      window.innerWidth - WIDGET_MENU_WIDTH - 8,
    );
    setMenuStyle({ top: rect.bottom + 6, left });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target) ||
        submenuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
      setExpanded(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (expanded) setExpanded(null);
        else setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, expanded]);

  const inUse = collectPanelIds(ctx.root);

  const isVariantAvailable = React.useCallback(
    (variant: BoutiqueVariant): boolean => {
      const def = PanelDefs[variant.panelId];
      if (!def) return false;
      if (def.multiInstance) return true;
      return !inUse.has(def.id);
    },
    [inUse],
  );

  const availableEntries: AvailableEntry[] = React.useMemo(() => {
    return getBoutiqueEntries()
      .map((entry) => ({
        entry,
        variants: entry.variants.filter(isVariantAvailable),
      }))
      .filter((e) => e.variants.length > 0);
  }, [isVariantAvailable]);

  const disabled = availableEntries.length === 0;

  const addVariant = (variant: BoutiqueVariant) => {
    const def = PanelDefs[variant.panelId];
    if (!def) return;
    if (def.multiInstance) {
      const newId = inUse.has(def.id) ? createPanelInstance(def.id) : def.id;
      ctx.actions.addPanel(newId);
    } else {
      ctx.actions.addPanel(def.id);
    }
    setOpen(false);
    setExpanded(null);
  };

  const onEntryClick = (available: AvailableEntry, btn: HTMLButtonElement) => {
    if (available.variants.length === 1) {
      addVariant(available.variants[0]!);
      return;
    }
    if (expanded?.entry.groupId === available.entry.groupId) {
      setExpanded(null);
      return;
    }
    const rect = btn.getBoundingClientRect();
    let left = rect.right + 4;
    if (left + SUBMENU_WIDTH > window.innerWidth - 8) {
      left = rect.left - SUBMENU_WIDTH - 4;
    }
    setExpanded({ entry: available.entry, top: rect.top, left });
  };

  const triggerClass = cn(
    headerBtnSecondary,
    variant === "sidebar" && "w-full justify-center px-1.5 py-1 text-[11.5px]",
    disabled && "cursor-not-allowed text-sf-text-faint opacity-60",
  );

  const menu =
    open && availableEntries.length > 0 && menuStyle ? (
      <div
        ref={menuRef}
        role="menu"
        className="fixed z-[9999] max-h-[min(60vh,320px)] w-60 overflow-y-auto rounded-[var(--sf-radius-lg)] border border-sf-border bg-white p-1 shadow-[var(--sf-shadow-lg)]"
        style={{ top: menuStyle.top, left: menuStyle.left }}
      >
        {availableEntries.map((available) => {
          const { entry, variants } = available;
          const Icn = entry.icon;
          const hasSubmenu = variants.length > 1;
          const isExpanded = expanded?.entry.groupId === entry.groupId;
          const def = PanelDefs[variants[0]!.panelId];
          const multiBadge = !hasSubmenu && def?.multiInstance;
          return (
            <button
              key={entry.groupId}
              type="button"
              role="menuitem"
              onClick={(e) => onEntryClick(available, e.currentTarget)}
              className={cn(
                "flex w-full cursor-pointer items-center gap-2.5 rounded-[5px] border-none px-2.5 py-2 text-left font-[inherit] text-[13px] text-sf-text",
                isExpanded
                  ? "bg-sf-primary-50"
                  : "bg-transparent hover:bg-gray-50",
              )}
              aria-haspopup={hasSubmenu ? "menu" : undefined}
              aria-expanded={hasSubmenu ? isExpanded : undefined}
            >
              <Icn size={14} />
              <span className="flex-1">{entry.title}</span>
              {multiBadge && (
                <span className="rounded bg-sf-primary/10 px-1 py-px text-[10px] font-bold text-sf-primary">
                  +
                </span>
              )}
              {hasSubmenu && (
                <I.ChevronRight size={12} className="text-sf-text-muted" />
              )}
            </button>
          );
        })}
      </div>
    ) : null;

  const submenu =
    open && expanded ? (
      <div
        ref={submenuRef}
        role="menu"
        className="fixed z-[9999] rounded-[var(--sf-radius-lg)] border border-sf-border bg-white p-1 shadow-[var(--sf-shadow-lg)]"
        style={{ top: expanded.top, left: expanded.left, width: SUBMENU_WIDTH }}
      >
        {expanded.entry.variants.filter(isVariantAvailable).map((variant) => {
          const def = PanelDefs[variant.panelId];
          const isMulti = def?.multiInstance;
          return (
            <button
              key={variant.panelId}
              type="button"
              role="menuitem"
              onClick={() => addVariant(variant)}
              className="flex w-full cursor-pointer items-center gap-2 rounded-[5px] border-none bg-transparent px-2.5 py-2 text-left font-[inherit] text-[13px] text-sf-text hover:bg-gray-50"
            >
              <span className="flex-1">{variant.label}</span>
              {isMulti && (
                <span className="rounded bg-sf-primary/10 px-1 py-px text-[10px] font-bold text-sf-primary">
                  +
                </span>
              )}
            </button>
          );
        })}
      </div>
    ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setOpen((o) => {
            const next = !o;
            if (next) updateMenuPosition();
            else setExpanded(null);
            return next;
          });
        }}
        disabled={disabled}
        className={triggerClass}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <I.Plus size={variant === "sidebar" ? 11 : 13} />
        {variant === "sidebar" ? "Ajouter widget" : "Ajouter un widget"}
      </button>
      {menu && createPortal(menu, document.body)}
      {submenu && createPortal(submenu, document.body)}
    </>
  );
}
